import React from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ChevronDown,
  ChevronRight,
  Clock,
  Eye,
  Calendar,
  Star,
  CheckCircle,
  AlertCircle,
  XCircle,
  Phone,
  Pause,
  X,
  ExternalLink
} from 'lucide-react'
import HoverSkillMatchContainer from '@/components/candidates/hover_SkillMatchContainer'
import CandidateDetails from '@/components/candidates/CandidateDetails'
import ApplicantActionButtons from './ApplicantActionButtons'

interface JobApplicant {
  id: number
  uuid: string
  application_id?: number // Optional for shortlisted candidates
  candidate_uuid?: string // For shortlisted candidates
  candidate_position_uuid?: string // UUID from candidate_position table
  first_name: string
  last_name: string
  full_name: string
  email: string
  phone?: string
  current_title?: string
  current_company?: string
  current_location?: string
  location?: string
  skills: string[] | any
  display_status?: string
  application_status?: string
  applied_at?: string
  last_updated?: string
  has_resume?: boolean
  resume_filename?: string
  // Additional fields for different candidate types
  total_experience_years?: number
  expected_salary?: number
  expected_salary_currency?: string
  linkedin_url?: string
  github_url?: string
  portfolio_url?: string
  resume_url?: string
  summary?: string
  source?: string
  applicant_type?: string
  position_title?: string
  company_name?: string
  resume_uuid?: string
  resume_file_path?: string
  resume_description?: string
}

interface PositionSkill {
  skill_id: number
  skill_name: string
  category: string
  proficiency_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  is_good_to_have?: boolean
}

interface PositionCertification {
  certification_name: string
  certification_body?: string
  is_required: boolean
  expiry_required?: boolean
}

interface TimelineEvent {
  type: string
  name: string
  date: string | null
  notes: string | null
  status: string
  byId: number | null
  byName: string | null
  metadata: any
}

interface ApplicantTableRowProps {
  applicant: JobApplicant
  positionSkills: PositionSkill[]
  positionCertifications: PositionCertification[]
  isExpanded: boolean
  isLoadingTimeline: boolean
  timeline: TimelineEvent[]
  updating?: number | null
  downloading?: number | null
  actingOnCandidate?: {[key: number]: string}
  onToggleExpanded: (applicantId: number) => void
  // Applicant-specific actions (optional for shortlisted candidates)
  onShortlist?: (applicationId: number) => void
  onUpdateStatus?: (applicationId: number, status: string) => void
  onDownloadResume?: (applicationId: number, filename?: string) => void
  // Shortlisted candidate actions (optional for applicants)
  onCandidateAction?: (candidateUuid: string, candidateId: number, action: string, notes?: string) => void
  onScheduleInterview?: (candidateId: string, candidateName: string, positionId?: string) => void
  positionId?: string
}

const STATUS_CONFIG = {
  // Applicant statuses
  new: { icon: Clock, color: 'bg-blue-100 text-blue-800', label: 'New' },
  reviewed: { icon: Eye, color: 'bg-yellow-100 text-yellow-800', label: 'Reviewed' },
  interview_scheduled: { icon: Calendar, color: 'bg-purple-100 text-purple-800', label: 'Interview Scheduled' },
  shortlisted: { icon: Star, color: 'bg-green-100 text-green-800', label: 'Shortlisted' },
  hired: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Hired' },
  rejected: { icon: AlertCircle, color: 'bg-red-100 text-red-800', label: 'Rejected' },
  on_hold: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', label: 'On Hold' },
  pending: { icon: Clock, color: 'bg-gray-100 text-gray-800', label: 'Pending' },
  // Shortlisted candidate statuses
  applied: { icon: AlertCircle, color: 'bg-blue-100 text-blue-800', label: 'Applied' },
  screening: { icon: Clock, color: 'bg-orange-100 text-orange-800', label: 'Screening' },
  interview1: { icon: Calendar, color: 'bg-purple-100 text-purple-800', label: 'Interview 1' },
  interview2: { icon: Calendar, color: 'bg-purple-100 text-purple-800', label: 'Interview 2' },
  interview3: { icon: Calendar, color: 'bg-purple-100 text-purple-800', label: 'Interview 3' },
  onhold: { icon: Pause, color: 'bg-yellow-100 text-yellow-800', label: 'On Hold' },
  archived: { icon: XCircle, color: 'bg-gray-100 text-gray-800', label: 'Archived' },
  active: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Active' }
}

export default function ApplicantTableRow({
  applicant,
  positionSkills,
  positionCertifications,
  isExpanded,
  isLoadingTimeline,
  timeline,
  updating,
  downloading,
  actingOnCandidate,
  onToggleExpanded,
  onShortlist,
  onUpdateStatus,
  onDownloadResume,
  onCandidateAction,
  onScheduleInterview,
  positionId
}: ApplicantTableRowProps) {
  const navigate = useNavigate()
  
  // Determine if this is an applicant or shortlisted candidate
  const isApplicant = !!applicant.application_id
  const currentStatus = applicant.display_status || applicant.application_status || 'pending'
  
  const statusConfig = STATUS_CONFIG[currentStatus as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pending
  const StatusIcon = statusConfig.icon

  return (
    <React.Fragment>
      <tr className="hover:bg-gray-50">
        {/* Candidate Name & Info */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div 
              className="flex-shrink-0 h-10 w-10 cursor-pointer"
              onClick={() => window.open(`/public/candidate-profile/${applicant.candidate_uuid || applicant.uuid}`, '_blank')}
            >
              <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold hover:bg-blue-600 transition-colors">
                {applicant.first_name?.[0]}{applicant.last_name?.[0]}
              </div>
            </div>
            <div className="ml-4">
              <div 
                className="text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                onClick={() => window.open(`/public/candidate-profile/${applicant.candidate_uuid || applicant.uuid}`, '_blank')}
              >
                {applicant.full_name}
              </div>
              <div className="text-sm text-gray-500">
                {applicant.current_title && applicant.current_company 
                  ? `${applicant.current_title} at ${applicant.current_company}`
                  : applicant.current_title || applicant.current_company || 'No position info'
                }
              </div>
            </div>
          </div>
        </td>

        {/* Contact */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-900">{applicant.email}</div>
          {applicant.phone && (
            <div className="text-sm text-gray-500">{applicant.phone}</div>
          )}
        </td>

        {/* Skill Match */}
        <td className="px-6 py-4">
          <HoverSkillMatchContainer 
            candidateSkills={applicant.skills || []}
            positionSkills={positionSkills}
            positionCertifications={positionCertifications}
            showExpanded={false}
            miniMode={true}
          />
        </td>

        {/* Current Status */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
            <StatusIcon className="w-4 h-4 mr-1" />
            <span className="ml-1 capitalize">{statusConfig.label}</span>
          </span>
        </td>

        {/* Actions */}
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
          {isApplicant && onShortlist && onUpdateStatus && onDownloadResume ? (
            <ApplicantActionButtons
              applicant={{
                ...applicant,
                application_id: applicant.application_id!,
                display_status: currentStatus,
                has_resume: applicant.has_resume || false
              }}
              updating={updating || null}
              downloading={downloading || null}
              onShortlist={onShortlist}
              onUpdateStatus={onUpdateStatus}
              onDownloadResume={onDownloadResume}
            />
          ) : onCandidateAction && onScheduleInterview ? (
            <div className="flex space-x-2">
              <button
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
                title="Audio Call"
                disabled={actingOnCandidate?.[applicant.id] === 'call'}
              >
                <Phone className="w-3 h-3 mr-1" />
                Call
              </button>
              <button
                onClick={() => onScheduleInterview(
                  applicant.id.toString(),
                  applicant.full_name,
                  positionId
                )}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded hover:bg-green-100"
                disabled={!!actingOnCandidate?.[applicant.id]}
              >
                <Calendar className="w-3 h-3 mr-1" />
                Schedule
              </button>
              <button
                onClick={() => onCandidateAction(
                  applicant.candidate_uuid || applicant.uuid,
                  applicant.id,
                  'hold'
                )}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-600 bg-yellow-50 border border-yellow-200 rounded hover:bg-yellow-100"
                disabled={!!actingOnCandidate?.[applicant.id]}
              >
                <Pause className="w-3 h-3 mr-1" />
                Hold
              </button>
              <button
                onClick={() => onCandidateAction(
                  applicant.candidate_uuid || applicant.uuid,
                  applicant.id,
                  'reject'
                )}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                disabled={!!actingOnCandidate?.[applicant.id]}
              >
                <X className="w-3 h-3 mr-1" />
                Reject
              </button>
            </div>
          ) : null}
        </td>

        {/* Details Toggle */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex flex-col space-y-2">
            <button
              onClick={() => onToggleExpanded(applicant.id)}
              className="flex items-center text-blue-600 hover:text-blue-900"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 mr-1" />
              ) : (
                <ChevronRight className="w-4 h-4 mr-1" />
              )}
              See Details
            </button>
            {/* Complete Detail Button - only for shortlisted candidates */}
            {!isApplicant && (
              <button
                onClick={() => {
                  console.log('🔍 Complete Detail button clicked for applicant:', {
                    candidate_position_uuid: applicant.candidate_position_uuid,
                    candidate_uuid: applicant.candidate_uuid,
                    uuid: applicant.uuid,
                    full_name: applicant.full_name,
                    entire_applicant_object: applicant
                  })
                  
                  // Use candidate_position_uuid if available, otherwise fall back to candidate_uuid or uuid
                  const candidateId = applicant.candidate_position_uuid || applicant.candidate_uuid || applicant.uuid
                  
                  console.log(`🔍 Using candidateId: ${candidateId} for navigation`)
                  console.log(`🔍 Navigation path: /recruiter/candidate-position-details/${candidateId}`)
                  
                  // Additional debugging
                  if (applicant.candidate_position_uuid) {
                    console.log('✅ SUCCESS: Using candidate_position_uuid for navigation')
                  } else if (applicant.candidate_uuid) {
                    console.log('⚠️  FALLBACK: Using candidate_uuid for navigation')
                  } else {
                    console.log('❌ PROBLEM: Falling back to user uuid for navigation')
                  }
                  
                  navigate(`/recruiter/candidate-position-details/${candidateId}`)
                }}
                className="flex items-center text-green-600 hover:text-green-900"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Complete Detail
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Expanded Details Row */}
      {isExpanded && (
        <tr>
          <td colSpan={6} className="p-0">
            <CandidateDetails
              candidate={{
                id: applicant.id,
                candidate_uuid: applicant.candidate_uuid || applicant.uuid,
                first_name: applicant.first_name,
                last_name: applicant.last_name,
                full_name: applicant.full_name,
                email: applicant.email,
                phone: applicant.phone,
                current_title: applicant.current_title,
                current_company: applicant.current_company,
                location: applicant.current_location || applicant.location,
                skills: applicant.skills,
                application_status: currentStatus,
                applied_at: applicant.applied_at,
                last_updated: applicant.last_updated
              }}
              positionSkills={positionSkills}
              positionCertifications={positionCertifications}
              isExpanded={isExpanded}
              isLoading={isLoadingTimeline}
              timeline={timeline}
            />
          </td>
        </tr>
      )}
    </React.Fragment>
  )
}